/**
@format
@flow
*/
import React from 'react';
import Colors from '@ziynx/theme/colors';
import TextLink from './TextLink';
import InlineLink from './InlineLink';
import NavLink from './NavLink';
import TextLinkCaret from './TextLinkCaret';

type Props = $ReadOnly<{|
  /**
   * Variant for link component
   */
  variant?: 'inline' | 'navigation' | 'default' | 'text' | 'caret',
  /**
   * Text for the link.
   */
  children?: any,

  /**
   * ExtraSmall NavLink
   */
  extraSmall?: boolean,

  /**
   * URL for the link.
   */
  href?: string,

  /**
   * Target for the link to open.
   */
  target?: string,

  /**
   * Custom color for inlineLink
   */
  color?: string,

  /**
   * Function to be fire when link is clicked.
   */
  onClick?: (event?: SyntheticEvent<HTMLAnchorElement>) => any | Promise<any>,
  /**
   * Routing path prop for React Router NavLink
   */
  to?: string | {
    pathname: string,
    search?: string, // Query parameters ex. ?query=true
    hash?: string, // ex '#hash'
    state: Object // ex  { isAdmin: true }
  },

  /**
   * Indicates if link is active
  */
  isActive?: () => any,
  /**
   * Label for the NavLink
   */
  label?: string,

  /**
  * Indicates link is active when it's exact
  */
  activeOnlyWhenExact?: boolean,

  /**
   * Sizes for Text Link
   */
  small?: boolean,
  medium?: boolean,
  large?: boolean,

  /**
   * Icon Position for TextLinkCaret
   */
  iconPosition?: 'left' | 'right',

  /**
   * Type of text
   */
  type?: 'body' | 'subheading' | 'heading',

|}>;

const Link = (props: Props): React$Node => {
  const {
    children,
    href,
    onClick,
    extraSmall,
    small,
    medium,
    large,
    color,
    variant,
    to,
    isActive,
    activeOnlyWhenExact,
    label,
    target,
    iconPosition,
    type,
    ...rest
  } = props;

  switch (variant) {
  case 'navigation':
    return (
      <NavLink
        activeOnlyWhenExact={activeOnlyWhenExact}
        extraSmall={extraSmall}
        color={color}
        to={to}
        label={label}
        isActive={isActive}
        {...rest}
      >
        {children}
      </NavLink>
    );
  case 'text':
    return (
      <TextLink
        color={color}
        href={href}
        onClick={onClick}
        target={target}
        small={small}
        medium={medium}
        large={large}
        type={type}
        {...rest}
      >
        {children}
      </TextLink>
    );
  case 'caret':
    return (
      <TextLinkCaret
        small={small}
        medium={medium}
        large={large}
        href={href}
        color={color}
        onClick={onClick}
        target={target}
        iconPosition={iconPosition}
      >
        {children}
      </TextLinkCaret>
    );
  case 'inline':
  default:
    return (
      <InlineLink
        color={color}
        href={href}
        onClick={onClick}
        target={target}
        {...rest}
      >
        {children}
      </InlineLink>
    );
  }
};

Link.defaultProps = {
  color: Colors.PrimaryBlue,
  variant: 'inline',
};

export default Link;
